import { useNavigate } from '@tanstack/react-router';
import cn from 'classnames';
import { useState, useCallback, useEffect, useMemo } from 'react';

import { useAppStore } from '@common/stores/useAppStore';
import { Button } from '@common/ui/buttons/Button';
import { IconButton } from '@common/ui/buttons/IconButton';
import { CachedImage } from '@common/ui/CachedImage';
import { Loader } from '@common/ui/Loader';
import { Result } from '@common/ui/Result';
import { Heading } from '@common/ui/typography/Heading';
import { Paragraph } from '@common/ui/typography/Paragraph';
import { useEventEmit } from '@common/utils/events';
import { postEvent } from '@common/utils/fetcher';

import { useAccountStore } from '@entities/account';
import { useAuthStore } from '@entities/auth/stores/useAuthStore';
import { useIncomingSuperlikes } from '@entities/dating/hooks/useIncomingSuperlikes';
import { LikeCard } from '@entities/likes/ui/LikeCard';
import { useSuperlikeSessionStore } from '@entities/superlike/model';

import { usePostSwipe } from '@services/hooks/MatchesService';
import { type User } from '@services/UsersService';

import { SuperlikesSection } from './components/SuperlikesSection';
import styles from './Likes.module.scss';

const MINIMUM_LOADING_TIME = 400;

export const Likes: React.FC = () => {
  const emit = useEventEmit();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { decrementLikesCount } = useAppStore();

  const { likes } = useAppStore();
  const { tasks } = useAppStore();
  const { trigger: triggerPostSwipe } = usePostSwipe();

  // Local loading state - show loader until we have initial data
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const isLoading = isInitialLoad && likes.length === 0;

  const [pendingActions, setPendingActions] = useState<Set<string>>(new Set());
  const [optimisticRemovals, setOptimisticRemovals] = useState<Set<string>>(
    new Set(),
  );
  const [processingActions, setProcessingActions] = useState<
    Map<string, 'like' | 'pass' | 'success'>
  >(new Map());
  const [originalActions, setOriginalActions] = useState<
    Map<string, 'like' | 'pass'>
  >(new Map());
  const [buttonPressed, setButtonPressed] = useState<Set<string>>(new Set());
  const [hasSwipedAll, setHasSwipedAll] = useState<boolean>(false);

  const visibleLikes =
    likes?.filter((profile) => !optimisticRemovals.has(profile.id)) || [];

  // Received superlikes, free for everyone, in their own section above the
  // grid (plan §2, review S10). Their senders also sent a like: the grid
  // leaves them out, and the senders answered this session. «Вы ответили
  // на все лайки» still counts on the whole list.
  const isSuperlikeEnabled = useAccountStore(
    (state) => state.account?.features.superlike === true,
  );
  // Dating is off (plan §3): nothing to answer here, and nothing to load.
  const isNotDating = useAccountStore(
    (state) => state.status === 'ready' && state.account?.dating === false,
  );
  const { cards: superlikeCards = [] } = useIncomingSuperlikes({
    isEnabled: isSuperlikeEnabled && !isNotDating,
  });
  const answeredSenderIds = useSuperlikeSessionStore(
    (state) => state.answeredSenderIds,
  );
  const superlikeSenderIds = new Set(superlikeCards.map((card) => card.id));
  const gridLikes = visibleLikes.filter(
    (profile) =>
      !superlikeSenderIds.has(profile.id) && !answeredSenderIds.has(profile.id),
  );
  const superlikesSection =
    superlikeCards.length > 0 ? (
      <SuperlikesSection cards={superlikeCards} />
    ) : null;

  const activeTasksCount = useMemo(() => {
    return (tasks || []).filter((task) => task.status === 'available')?.length;
  }, [tasks]);

  const currentButtons = useMemo(() => {
    if (user?.is_premium && activeTasksCount === 0) {
      return null;
    }

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          width: '100%',
        }}
      >
        {activeTasksCount > 0 && (
          <Button
            testId="likes-tasks-button"
            variant="premiumGradient"
            size={1000}
            onClick={() => navigate({ to: '/tasks' })}
          >
            Выполнить задания
          </Button>
        )}

        {!user?.is_premium && (
          <Button
            testId="likes-premium-button"
            variant={
              activeTasksCount > 0 ? 'actionSecondary' : 'premiumGradient'
            }
            size={1000}
            onClick={() => navigate({ to: '/premium' })}
          >
            Подключить Premium
          </Button>
        )}
      </div>
    );
  }, [activeTasksCount, user]);

  // Stop showing initial loader once we have data (even if empty array)
  useEffect(() => {
    if (likes !== undefined) {
      setIsInitialLoad(false);
    }
  }, [likes]);

  useEffect(() => {
    if (likes && !isLoading) {
      setOptimisticRemovals((prev) => {
        const newSet = new Set(prev);
        const currentLikeIds = new Set(likes.map((like) => like.id));

        prev.forEach((userId) => {
          if (!currentLikeIds.has(userId)) {
            newSet.delete(userId);
          }
        });

        return newSet;
      });
    }
  }, [likes, isLoading]);

  useEffect(() => {
    if (likes && likes.length > 0 && visibleLikes.length === 0) {
      setHasSwipedAll(true);
    }
  }, [likes, visibleLikes.length]);

  useEffect(() => {
    if (!user?.is_premium && !isNotDating) {
      postEvent('first_paywall_impression', { source: 'likes_locked' }, true);
      postEvent('paywall_cycle_hit', { source: 'likes_locked' });
    }
  }, []);

  const onAction = useCallback(
    async (action: 'like' | 'pass', profile: User) => {
      const profileId = profile.id;

      if (!user?.is_premium) {
        return navigate({ to: '/premium' });
      }

      if (user?.approval_status?.status !== 'approved') {
        return emit('warning', {
          title: 'Ваш профиль не подтвержден',
          description:
            'Вы сможете лайкать пользователей в ответ после подтверждения анкеты модератором',
        });
      }

      if (pendingActions.has(profileId)) {
        return;
      }

      setButtonPressed((prev) => new Set([...prev, profileId]));
      setTimeout(() => {
        setButtonPressed((prev) => {
          const newSet = new Set(prev);
          newSet.delete(profileId);
          return newSet;
        });
      }, 200);

      setPendingActions((prev) => new Set([...prev, profileId]));
      setProcessingActions((prev) => new Map([...prev, [profileId, action]]));
      setOriginalActions((prev) => new Map([...prev, [profileId, action]]));

      // Optimistic updates for both like and pass actions
      if (action === 'pass') {
        decrementLikesCount();
      } else if (action === 'like') {
        // For like actions from likes page, always increment unread chats (it's always a like-back)
        const { optimisticallyIncrementUnreadChats } = useAppStore.getState();
        optimisticallyIncrementUnreadChats();
        decrementLikesCount();
      }

      const startTime = Date.now();

      try {
        await triggerPostSwipe({
          target_user_id: profile.id,
          action,
          isLikeBack: action === 'like',
        });

        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, MINIMUM_LOADING_TIME - elapsedTime);

        if (remainingTime > 0) {
          await new Promise((resolve) => setTimeout(resolve, remainingTime));
        }

        setProcessingActions(
          (prev) => new Map([...prev, [profileId, 'success']]),
        );
        await new Promise((resolve) => setTimeout(resolve, 300));

        setOptimisticRemovals((prev) => new Set([...prev, profileId]));

        setTimeout(() => {
          setPendingActions((prev) => {
            const newSet = new Set(prev);
            newSet.delete(profileId);
            return newSet;
          });
          setProcessingActions((prev) => {
            const newMap = new Map(prev);
            newMap.delete(profileId);
            return newMap;
          });
          setOriginalActions((prev) => {
            const newMap = new Map(prev);
            newMap.delete(profileId);
            return newMap;
          });
        }, 300);
      } catch (error) {
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, MINIMUM_LOADING_TIME - elapsedTime);

        if (remainingTime > 0) {
          await new Promise((resolve) => setTimeout(resolve, remainingTime));
        }

        // If the swipe action fails, SWR's revalidation will eventually correct the count.
        // No need for manual rollback of likes count.
        // The rollback for chat count is handled within the useSwipe hook.

        setPendingActions((prev) => {
          const newSet = new Set(prev);
          newSet.delete(profileId);
          return newSet;
        });
        setProcessingActions((prev) => {
          const newMap = new Map(prev);
          newMap.delete(profileId);
          return newMap;
        });
        setOriginalActions((prev) => {
          const newMap = new Map(prev);
          newMap.delete(profileId);
          return newMap;
        });

        emit('error', {
          title: 'Ошибка отправки запроса',
          description: 'Попробуйте позже',
        });
      }
    },
    [
      user,
      navigate,
      emit,
      triggerPostSwipe,
      pendingActions,
      decrementLikesCount,
    ],
  );

  if (isNotDating)
    return (
      <Result
        src={'/icons/3d-icons/like.png'}
        title="Вы сейчас не знакомитесь"
        iconContainerBg="var(--bg-action-secondary-transparent)"
      />
    );

  if (isLoading)
    return (
      <>
        {superlikesSection}
        <Loader />
      </>
    );

  if (hasSwipedAll || (visibleLikes.length === 0 && likes && likes.length > 0))
    return (
      <>
        {superlikesSection}
        <Result
          src={'/icons/3d-icons/likes.png'}
          title="Отлично!"
          description="Вы ответили на все лайки"
          iconContainerBg="#FF5C8C12"
        />
      </>
    );

  if (visibleLikes.length === 0 && likes?.length === 0 && !hasSwipedAll)
    return (
      <>
        {superlikesSection}
        <Result
          src={'/icons/3d-icons/likes.png'}
          title="Здесь будут лайки"
          description="Совсем скоро"
          iconContainerBg="#FF5C8C12"
        />
      </>
    );

  return (
    <>
      {superlikesSection}
      <div style={{ paddingTop: '16px' }}>
        {!user.is_premium && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0,
            }}
          >
            <CachedImage
              testId="cached-image"
              src="/icons/3d-icons/likes.png"
              alt="result-image"
              style={{ width: 96, height: 96 }}
            />
            <Heading level={2} noPadding style={{ padding: '8px 0' }}>
              Откройте лайки
            </Heading>
            <Paragraph compact align="center" variant="secondary">
              Premium даст вам возможность увидеть тех, кто вас лайкнул
            </Paragraph>
            {currentButtons}
          </div>
        )}

        {user.is_premium && (
          <Paragraph compact variant="secondary" align="center">
            Эти люди лайкнули тебя. Может быть среди них твой мэтч?
          </Paragraph>
        )}

        <div
          style={{
            display: gridLikes.length <= 1 ? 'block' : 'flex',
            rowGap: '16px',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            marginTop: 32,
          }}
        >
          {gridLikes.map((profile) => {
            const profileId = profile.id;
            const isPending = pendingActions.has(profileId);
            const processingAction = processingActions.get(profileId);
            const originalAction = originalActions.get(profileId);
            const isPressed = buttonPressed.has(profileId);

            const likeBtnClassNames = cn({
              [styles.actionButton]: true,
              [styles.likeBtn]: true,
              [styles.processing]: isPressed,
              [styles.successLike]:
                processingAction === 'success' && originalAction === 'like',
            });

            const likeIconWrapperClassName = cn(styles.iconTransition, {
              [styles.hiding]: isPending && processingAction === 'like',
              [styles.successLike]:
                processingAction === 'success' && originalAction === 'like',
            });

            const likeBtnSpinnerClassName = cn(styles.spinnerContainer, {
              [styles.showing]: isPending && processingAction === 'like',
            });

            return (
              <LikeCard
                key={profileId}
                profile={profile}
                showActions={true}
                className={`${styles.likeCard} ${isPending ? styles.pending : ''} ${optimisticRemovals.has(profileId) ? styles.removing : ''}`}
                onClick={() => {
                  if (!isPending) {
                    navigate({
                      to: `/dating/profile/${profileId}`,
                      search: { canSwipes: true },
                    });
                  }
                }}
                likeButton={
                  <IconButton
                    testId="likes-like-button"
                    variant="actionPrimary"
                    disabled={isPending}
                    onClick={(event) => {
                      event.stopPropagation();
                      onAction('like', profile);
                    }}
                    className={likeBtnClassNames}
                  >
                    <div className={styles.buttonContent}>
                      <div className={likeIconWrapperClassName}>
                        <CachedImage
                          testId="cached-image"
                          src="/icons/3d-icons/like.png"
                          alt="like"
                        />
                      </div>
                      <div className={likeBtnSpinnerClassName}>
                        <div className={styles.loadingSpinner} />
                      </div>
                    </div>
                  </IconButton>
                }
                dislikeButton={
                  <IconButton
                    testId="likes-dislike-button"
                    variant="negativeSecondary"
                    disabled={isPending}
                    onClick={(event) => {
                      event.stopPropagation();
                      onAction('pass', profile);
                    }}
                    className={`${styles.dislikeBtn} ${styles.actionButton} ${isPressed ? styles.processing : ''} ${processingAction === 'success' && originalAction === 'pass' ? styles.successPass : ''}`}
                  >
                    <div className={styles.buttonContent}>
                      <div
                        className={`${styles.iconTransition} ${isPending && processingAction === 'pass' ? styles.hiding : ''} ${processingAction === 'success' && originalAction === 'pass' ? styles.successPass : ''}`}
                      >
                        <CachedImage
                          testId="cached-image"
                          src="/icons/3d-icons/dislike.png"
                          alt="dislike"
                        />
                      </div>
                      <div
                        className={`${styles.spinnerContainer} ${isPending && processingAction === 'pass' ? styles.showing : ''}`}
                      >
                        <div className={styles.loadingSpinner} />
                      </div>
                    </div>
                  </IconButton>
                }
                isLocked={!user.is_premium}
              />
            );
          })}
        </div>
      </div>
    </>
  );
};

Likes.displayName = 'Likes';
